import { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

// Our Components
import FinancialGoalDropdown from 'components/Dropdown/FinancialGoalDropdown';
import FinancialLiabilityForm from 'components/Forms/Liabilities/Common/FinancialLiabilityForm';
import FormButton from 'components/Forms/FormButton';
import LiabilityFormHeading from 'components/Forms/Liabilities/LiabilityFormHeading';
import LiabilityFormContainer from 'components/Forms/Liabilities/Common/LiabilityFormContainer';
import Loader from 'components/Loader';

// Utils
import {
	dateToIsoFormat,
	FINANCIAL_GOAL_TO_ENUM_MAPPING,
	getDefaultApproxPayoffDate,
	isSubmissionReady,
	roundToDecimal
} from 'shared/utils';
import formatLoanType from 'shared/utils/formatting/liability/formatLoanType';

// our hooks
import useGetUserPersonalLiability from 'hooks/client/liabilities/queries/useGetUserPersonalLiability';
import useMutateSavePersonalLiability from 'hooks/client/liabilities/mutations/useMutateSavePersonalLiability';
import usePrepopulateState from 'hooks/prepopulate/usePrepopulateState';

// Constants
import { PERSONAL } from 'shared/constants';

function PersonalLiability() {
	const navigate = useNavigate();
	const savePersonalLoan = useMutateSavePersonalLiability();
	const { isLoading, isSuccess, data } = useGetUserPersonalLiability();

	const { isLoading: isMutationLoading } = savePersonalLoan;

	// Personal Liability Related
	const [approxPayoffDate, setApproxPayoffDate] = useState(
		getDefaultApproxPayoffDate(PERSONAL)
	);
	const [lender, setLender] = useState('');
	const [loanType, setLoanType] = useState('Fixed');
	const [monthlyPayment, setMonthlyPayment] = useState('');
	const [ownershipTenure, setOwnershipTenure] = useState('');
	const [outstandingBalance, setOutstandingBalance] = useState(''); // balance / 100 is bc methodfi provides balance in cents so we divide by 100 to convert into dollars
	const [rate, setRate] = useState('');

	// Personal Related Data
	const [loanPurpose, setLoanPurpose] = useState('');

	// Financial Goal
	const [financialGoal, setFinancialGoal] = useState(
		'Reduce total cost of debt'
	);

	usePrepopulateState(
		isSuccess
			? [
					{
						setState: setOutstandingBalance,
						value: `${data?.outstandingBalance ?? ''}`
					},
					{ setState: setRate, value: `${data?.interestRate ?? ''}` },
					{
						setState: setApproxPayoffDate,
						value: `${data?.expectedPayOffDate ?? ''}`
					},
					{ setState: setLender, value: data?.lender },
					{
						setState: setMonthlyPayment,
						value: `${data?.monthlyPay ?? ''}`
					},
					{
						setState: setOwnershipTenure,
						value: `${Math.floor(data?.tenureMonth ?? 0) / 12}`
					},
					{ setState: setLoanType, value: data?.loanType },
					{ setState: setLoanPurpose, value: data?.loanUserFor },
					{ setState: setFinancialGoal, value: data?.financialGoal },
					{
						setState: setLoanType,
						value: formatLoanType(data?.loanType ?? '')
					}
			  ]
			: [],
		[data]
	);

	const requiredFormValues = [
		approxPayoffDate,
		financialGoal,
		lender,
		monthlyPayment,
		outstandingBalance,
		ownershipTenure,
		rate
	];

	const isFormReady = useMemo(
		() =>
			isSubmissionReady(requiredFormValues) && approxPayoffDate !== null,
		requiredFormValues
	);

	const handleApproxPayoffDateChange = (value) => {
		const isValueEmpty = value === '';
		if (isValueEmpty) {
			setApproxPayoffDate('');
			return;
		}
		setApproxPayoffDate(value);
	};

	const submitLiabilityData = (route) => {
		const formattedApproxPayOffDate = dateToIsoFormat(approxPayoffDate);
		const formattedFinancialGoal =
			FINANCIAL_GOAL_TO_ENUM_MAPPING(financialGoal);
		const formattedLoanType = loanType.toUpperCase();
		const formattedRate = roundToDecimal(+rate, 2);

		const personalLoan = {
			approxPayoffDate: formattedApproxPayOffDate,
			financialGoal: formattedFinancialGoal,
			lender,
			loanType: formattedLoanType,
			personalLoanUse: loanPurpose,
			monthlyPayment: +monthlyPayment,
			outstandingBalance: +outstandingBalance,
			rate: formattedRate,
			tenure: +ownershipTenure,
			tradelineType: PERSONAL // can be one of AUTO | MORTGAGE | PERSONAL | STUDENT
		};

		savePersonalLoan.mutate(personalLoan, {
			onSuccess: () => {
				navigate(route);
			}
		});
	};

	if (isLoading) {
		return (
			<>
				<Helmet>
					<title>Personal Loan</title>
				</Helmet>
				{/* Once data binding begins we will add Mortgage specific information to this header */}
				<LiabilityFormHeading headingText="Personal Loan" />
				<Loader
					size={60}
					boxSX={{ alignItems: 'center', marginTop: 15 }}
				/>
			</>
		);
	}
	return (
		<>
			<Helmet>
				<title>Personal Loan</title>
			</Helmet>

			{/* Once data binding begins we will add Mortgage specific information to this header */}
			<LiabilityFormHeading headingText="Personal Loan" />

			<LiabilityFormContainer>
				<FinancialLiabilityForm
					outstandingBalance={outstandingBalance}
					setOutstandingBalance={setOutstandingBalance}
					monthlyPayment={monthlyPayment}
					setMonthlyPayment={setMonthlyPayment}
					rate={rate}
					setRate={setRate}
					approxPayoffDate={approxPayoffDate}
					handleApproxPayoffDateChange={handleApproxPayoffDateChange}
					ownershipTenure={ownershipTenure}
					setOwnershipTenure={setOwnershipTenure}
					loanType={loanType}
					setLoanType={setLoanType}
					lender={lender}
					setLender={setLender}
				/>

				<FinancialGoalDropdown
					withoutCashout
					financialGoal={financialGoal}
					setFinancialGoal={setFinancialGoal}
				/>

				<FormButton
					isFormReady={isFormReady}
					isMutationLoading={isMutationLoading}
					submitLiabilityData={submitLiabilityData}
				/>
			</LiabilityFormContainer>
		</>
	);
}

export default PersonalLiability;
